import {
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import {
  createSelector
} from '@reduxjs/toolkit';
import {
  isEmpty
} from '@shared/lib';
import {
  sessionSlice,
  selectUserStatus,
  type SessionState
} from '@entities/session';
import {
  type HowItWorksBlockType
} from '@widgets/how-it-works-block';
import {
  type Referral
} from '../lib';
import {
  referAFriendPageSlice,
  // TODO: remove all duplicated types
  type ActionItem,
  type ReferAFriendPageState,
  type ReferAFriendPageUserCarouselSliderItem,
} from './slice';

type RootState = {
  [sessionSlice.name]: SessionState;
  [referAFriendPageSlice.name]: ReferAFriendPageState;
};

export const selectReferralLink = (currentHost: Maybe<Url>) => (state: RootState): Url => {
  const referralCode = state[referAFriendPageSlice.name].referralCode;
  if (!referralCode) return 'no link';

  const url = new URL(`${currentHost}/sign-up`);
  url.searchParams.append('referralCode', referralCode);

  return url.toString();
};

export const selectPromotionsPageMetaTitle = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name]?.title
);

export const selectPromotionsPageMetaDescription = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name]?.description
);

export const selectPromotionsPageMetaKeywords = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name]?.keywords
);

export const selectPromotionsPageMetaCanonical = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name]?.canonical
);

export const selectReferAFriendPageSlider = createSelector(
  selectUserStatus,
  (state: RootState) => (state[referAFriendPageSlice.name]?.slider),
  (userState, slider) => {
    const _slider = slider as unknown as Maybe<Array<ReferAFriendPageUserCarouselSliderItem & { actions: Array<ActionItem>, }>>;
    const sliderWithActionByUserStatus = _slider?.map(({ actions, ...props }) => {
      const actionByUserStatus = actions?.find(actionItem => actionItem.userState === userState);
      return ({ ...props, action: actionByUserStatus });
    });
    return sliderWithActionByUserStatus;
  },
  { devModeChecks: { identityFunctionCheck: 'never' } }
);

export const selectReferAFriendPageReferralLinkFieldTitle = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].referralLinkFieldTitle
);

export const selectReferAFriendPageSocialShareBarTitle = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].socialShareBarTitle
);

export const selectReferAFriendPageFacebookShareText = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].facebookShareText
);

export const selectReferAFriendPageTwitterShareText = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].twitterShareText
);

export const selectReferAFriendPageWhatsappShareText = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].whatsappShareText
);

export const selectReferAFriendPageEmailShareSubject = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].emailShareSubject
);

export const selectReferAFriendPageEmailShareBody = (state: RootState): Maybe<string> => (
  state[referAFriendPageSlice.name].emailShareBody
);

export const selectHowItWorksBlock = (state: RootState): Maybe<HowItWorksBlockType> => (
  state[referAFriendPageSlice.name]?.howItWorksBlock
);

export const selectReferees = (state: RootState): Maybe<Array<Referral>> => (
  state[referAFriendPageSlice.name]?.referees
);

export const selectHasReferees = (state: RootState): boolean => (
  !isEmpty(state[referAFriendPageSlice.name]?.referees)
);

export const useReferAFriendPageSelector: TypedUseSelectorHook<RootState> = useSelector;
